import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import {
  fetchClientesParaMetricas,
  fetchOrdensParaMetricas,
  fetchOrigensRaw,
  listOrdensServicoCliente,
  agruparMetricasPorCliente,
  combinarClientesComMetricas,
  extrairOrigensUnicas,
  aplicarFiltrosClientes,
  type ClienteReal,
  type FiltrosClientesReais,
  type StatusLedCliente,
} from '@/services/clientes-reais.service';

export type { ClienteReal, FiltrosClientesReais, StatusLedCliente };

export function useClientesReais(filtros?: FiltrosClientesReais) {
  const { profile, isAdmin } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['clientes-reais', organizationId, filtros, profile?.id, isAdmin],
    queryFn: async () => {
      const [clientes, ordens] = await Promise.all([
        fetchClientesParaMetricas(),
        fetchOrdensParaMetricas(),
      ]);
      const metricsMap = agruparMetricasPorCliente(ordens);
      const clientesComMetricas = combinarClientesComMetricas(clientes, metricsMap);
      return aplicarFiltrosClientes(clientesComMetricas, filtros, {
        profileId: profile?.id,
        isAdmin,
      });
    },
    enabled: !!profile,
  });
}

export function useOrigensClientes() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['origens-clientes', organizationId],
    queryFn: async () => {
      const rows = await fetchOrigensRaw();
      return extrairOrigensUnicas(rows);
    },
  });
}

export function useOrdensServicoCliente(clienteId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['ordens-servico-cliente', organizationId, clienteId],
    queryFn: () => (clienteId ? listOrdensServicoCliente(clienteId) : Promise.resolve([])),
    enabled: !!clienteId,
  });
}
